import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronRight, Star, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { useMe } from '@/auth/useAuth';
import { api } from '@/lib/api';
import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';
import { PORTAL_NUMBER_LOCALE } from '@/lib/i18n/portalLocale';
import { useWsEvent } from '@/lib/useWsEvent';

import {
  PORTAL_NAV_ITEM_IDS,
  PORTAL_WALLET_MENU_ITEMS,
  isPortalWalletPath,
  type PortalWalletMenuItem,
} from './portalNavigation';
import { closeWhenAnotherPortalOverlayOpens, openPortalOverlay } from './portalOverlay';

const STARS_MENU_ID = 'portal-stars-menu';
const STARS_MENU_TITLE_ID = 'portal-stars-menu-title';
const DESKTOP_MENU_MEDIA_QUERY = '(min-width: 1024px)';
const FOCUSABLE_ELEMENT_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface WalletBalance {
  stars_balance: number;
}

interface PortalStarsMenuProps {
  onFullscreenOpenChange?: (isOpen: boolean) => void;
}

function readIsDesktopMenu(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DESKTOP_MENU_MEDIA_QUERY).matches
    : false;
}

function useIsDesktopMenu(): boolean {
  const [isDesktopMenu, setIsDesktopMenu] = useState(readIsDesktopMenu);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia(DESKTOP_MENU_MEDIA_QUERY);
    const updateMenuMode = (event: MediaQueryListEvent) => setIsDesktopMenu(event.matches);

    setIsDesktopMenu(mediaQuery.matches);
    mediaQuery.addEventListener('change', updateMenuMode);
    return () => mediaQuery.removeEventListener('change', updateMenuMode);
  }, []);

  return isDesktopMenu;
}

function isWalletMenuItemActive(item: PortalWalletMenuItem, pathname: string): boolean {
  if (item.id === PORTAL_NAV_ITEM_IDS.USAGE) {
    return matchPath({ path: '/portal/usage/*', end: true }, pathname) !== null;
  }
  return pathname === item.to || pathname === `${item.to}/`;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR));
}

interface StarsMenuContentProps {
  balance: number | null;
  hasFamily: boolean;
  isDesktopMenu: boolean;
  isError: boolean;
  isLoading: boolean;
  pathname: string;
  onClose: () => void;
  onRetry: () => void;
  closeButtonRef: React.RefObject<HTMLButtonElement>;
  menuRef: React.RefObject<HTMLElement>;
}

function StarsMenuContent({
  balance,
  hasFamily,
  isDesktopMenu,
  isError,
  isLoading,
  pathname,
  onClose,
  onRetry,
  closeButtonRef,
  menuRef,
}: StarsMenuContentProps) {
  const { locale, t } = usePortalLocale();
  const balanceText =
    balance === null ? '—' : new Intl.NumberFormat(PORTAL_NUMBER_LOCALE[locale]).format(balance);

  return (
    <section
      ref={menuRef}
      id={STARS_MENU_ID}
      role="dialog"
      aria-modal="true"
      aria-labelledby={STARS_MENU_TITLE_ID}
      className={clsx(
        isDesktopMenu
          ? 'absolute right-0 top-[calc(100%+0.75rem)] z-50 w-[390px] overflow-hidden rounded-3xl border border-ink/15 bg-canvas-pure p-3 shadow-floating-chrome'
          : 'fixed inset-0 z-[70] min-h-dvh overflow-y-auto overscroll-contain bg-canvas pb-[calc(1.5rem+env(safe-area-inset-bottom))]',
      )}
      data-presentation={isDesktopMenu ? 'desktop-popover' : 'mobile-fullscreen'}
      data-testid="portal-stars-menu"
    >
      <div
        className={clsx(
          'flex items-center justify-between gap-4',
          isDesktopMenu
            ? 'px-2 pb-3 pt-1'
            : 'sticky top-3 z-10 mx-3 mt-3 rounded-full border border-hairline bg-canvas-pure/95 px-4 py-2.5 shadow-card-soft backdrop-blur',
        )}
      >
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate2">
            {t('Parent Portal')}
          </p>
          <h2 id={STARS_MENU_TITLE_ID} className="truncate text-lg font-bold text-ink">
            {t('Stars & usage')}
          </h2>
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate2 transition-colors hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral"
          aria-label={t('Close Stars menu')}
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div
        className={clsx(
          'rounded-3xl border border-brand-sunshine/45 bg-wash-sunshine',
          isDesktopMenu ? 'mb-2 px-5 py-4' : 'mx-4 mt-4 px-5 py-5',
        )}
      >
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-canvas-pure text-ink shadow-card-soft">
            <Star className="h-6 w-6 fill-current" strokeWidth={1.8} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate2">
              {t('Available now')}
            </p>
            <p className="mt-0.5 text-2xl font-bold tabular-nums text-ink">
              {isLoading ? t('Loading…') : t('{balance} Stars', { balance: balanceText })}
            </p>
          </div>
        </div>

        <div className="mt-4 border-t border-brand-sunshine/45 pt-4">
          <p className="text-sm leading-5 text-ink-soft">
            {t('Like petrol for a car, Stars are prepaid fuel for the AI tools your child uses.')}
          </p>
          <Link
            to="/portal/wallet/stars-guide"
            onClick={onClose}
            className="mt-2 inline-flex min-h-11 items-center gap-1.5 py-2 text-sm font-bold text-ink underline decoration-brand-sunshine decoration-2 underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral"
          >
            {t('How Stars and AI tokens work')}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        {!hasFamily && (
          <div className="mt-4 border-t border-brand-sunshine/45 pt-4">
            <p className="text-sm font-semibold text-ink">{t('Set up your family to use Stars.')}</p>
            <Link to="/portal/register" onClick={onClose} className="btn-pill-secondary mt-3">
              {t('Start family setup')}
            </Link>
          </div>
        )}

        {isError && hasFamily && (
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-brand-sunshine/45 pt-4">
            <p className="text-sm font-semibold text-ink">{t('Balance is unavailable.')}</p>
            <button type="button" onClick={onRetry} className="btn-pill-secondary min-h-11">
              {t('Try again')}
            </button>
          </div>
        )}
      </div>

      <nav
        aria-label={t('Stars and usage actions')}
        className={clsx(
          isDesktopMenu ? 'grid gap-1' : 'mx-auto grid w-full max-w-xl gap-2 px-4 py-4',
        )}
      >
        {PORTAL_WALLET_MENU_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = isWalletMenuItemActive(item, pathname);

          return (
            <Link
              key={item.id}
              to={item.to}
              onClick={onClose}
              aria-current={isActive ? 'page' : undefined}
              className={clsx(
                'group flex min-w-0 items-center gap-3 rounded-2xl font-semibold text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral',
                isDesktopMenu
                  ? 'min-h-[58px] px-3 py-2.5 text-sm'
                  : 'min-h-16 border border-hairline bg-canvas-pure px-4 py-3 text-[15px]',
                isActive ? 'border-brand-sunshine/60 bg-wash-sunshine' : 'hover:bg-canvas',
              )}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-canvas text-slate2 transition-colors group-hover:text-ink">
                <Icon className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block leading-5">{t(item.label)}</span>
                <span className="mt-0.5 block text-xs font-medium leading-4 text-slate2">
                  {t(item.description)}
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate2" aria-hidden="true" />
            </Link>
          );
        })}
      </nav>
    </section>
  );
}

export function PortalStarsMenu({ onFullscreenOpenChange }: PortalStarsMenuProps) {
  const me = useMe('user');
  const queryClient = useQueryClient();
  const location = useLocation();
  const isDesktopMenu = useIsDesktopMenu();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const familyId = me.data?.kind === 'user' ? me.data.family_id : null;
  const wallet = useQuery<WalletBalance>({
    queryKey: ['wallet', familyId],
    queryFn: () => api<WalletBalance>(`/families/${familyId}/wallet`),
    enabled: !!familyId,
  });
  const { locale, t } = usePortalLocale();
  const balance = wallet.data?.stars_balance ?? null;
  const visibleBalance =
    balance === null
      ? wallet.isLoading
        ? '…'
        : '—'
      : balance.toLocaleString(PORTAL_NUMBER_LOCALE[locale]);
  const accessibleBalance = wallet.isLoading
    ? t('Stars balance loading')
    : wallet.isError
      ? t('Stars balance unavailable')
      : !familyId
        ? t('Stars unavailable until family setup')
        : t('{balance} Stars', { balance: balance ?? 0 });
  const isWalletRoute = isPortalWalletPath(location.pathname);

  useWsEvent(
    'wallet.update',
    () => {
      if (familyId) void queryClient.invalidateQueries({ queryKey: ['wallet', familyId] });
    },
    [familyId, queryClient],
    'user',
  );

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => closeWhenAnotherPortalOverlayOpens('stars', () => setIsOpen(false)), []);

  useEffect(() => {
    onFullscreenOpenChange?.(isOpen && !isDesktopMenu);
    return () => onFullscreenOpenChange?.(false);
  }, [isDesktopMenu, isOpen, onFullscreenOpenChange]);

  useEffect(() => {
    if (!isOpen) return;

    const returnFocusTarget = triggerRef.current;
    closeButtonRef.current?.focus();

    const closeOrTrapFocus = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !menuRef.current) return;

      const focusableElements = getFocusableElements(menuRef.current);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', closeOrTrapFocus);
    return () => {
      document.removeEventListener('keydown', closeOrTrapFocus);
      returnFocusTarget?.focus();
    };
  }, [isOpen]);

  const menu = isOpen ? (
    <StarsMenuContent
      balance={balance}
      hasFamily={!!familyId}
      isDesktopMenu={isDesktopMenu}
      isError={wallet.isError}
      isLoading={wallet.isLoading}
      pathname={location.pathname}
      onClose={() => setIsOpen(false)}
      onRetry={() => void wallet.refetch()}
      closeButtonRef={closeButtonRef}
      menuRef={menuRef}
    />
  ) : null;

  return (
    <div className="relative ml-auto shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls={STARS_MENU_ID}
        aria-haspopup="dialog"
        aria-current={isWalletRoute ? 'page' : undefined}
        aria-label={t('{balance}. Open Stars menu', { balance: accessibleBalance })}
        onClick={() => {
          openPortalOverlay('stars');
          setIsOpen((current) => !current);
        }}
        className={clsx(
          'isolate flex min-h-11 items-center gap-2 rounded-full border px-3 text-sm font-bold tabular-nums [transform:translateZ(0)] [backface-visibility:hidden] transition-[background-color,border-color,transform] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral focus-visible:ring-offset-2 focus-visible:ring-offset-canvas-pure sm:px-4',
          isOpen || isWalletRoute
            ? 'border-brand-sunshine/65 bg-wash-sunshine text-ink'
            : 'border-hairline bg-canvas-pure text-ink hover:border-brand-sunshine/65 hover:bg-wash-sunshine',
        )}
        data-testid="portal-stars-trigger"
      >
        <Star className="h-5 w-5 fill-current" strokeWidth={1.8} aria-hidden="true" />
        <span className="whitespace-nowrap">{visibleBalance}</span>
      </button>

      {isOpen && isDesktopMenu && (
        <button
          type="button"
          aria-label={t('Dismiss Stars menu')}
          className="fixed inset-0 z-40 bg-transparent"
          onClick={() => setIsOpen(false)}
        />
      )}
      {isOpen && !isDesktopMenu ? createPortal(menu, document.body) : menu}
    </div>
  );
}
